/**
 * Interface representing the object that is supposed to be returned by @type {TimeParser} class.
 */
export interface ITime {
  hours: number;
  minutes: number;
  /**
   * If this is null, it means that the time is in 24 hour format. Otherwise, it is in AM/PM format.
   */
  meridiem: 'AM' | 'PM' | null;
}

export interface ITimeParserError {
  message: string;
}

export default class TimeParser {
  private static isInteger(c: number) {
    return c >= 48 && c <= 57;
  }

  private contents = '';
  private offset = 0;

  public parse(contents: string): ITimeParserError | ITime {
    this.offset = 0;
    /**
     * remove all white spaces
     */
    this.contents = contents
      .split('')
      .map((v) => v.trim())
      .filter((s) => s.length > 0)
      .join('');

    const hours = this.parseInteger();
    if (hours === null) {
      return { message: 'Time format should be in either 3:00 PM or 15:00' };
    }

    if (!this.read(':')) {
      return { message: 'After hours, it should be preceded by :' };
    }

    const minutes = this.parseInteger();
    if (minutes === null) {
      return { message: 'After :, minutes should be provided' };
    }

    if (minutes.value > 59) {
      return { message: 'Minutes should be between 0 and 59' };
    }

    const isAM = this.read('AM', 'am');
    const isPM = this.read('PM', 'pm');

    if (isAM || isPM) {
      if (hours.value < 1 || hours.value > 12) {
        return {
          message:
            'If time is entered as AM/PM time, hours should be between 1 and 12'
        };
      }
    }

    if (!this.eof()) {
      const lenDiff = contents.length - this.contents.length;
      return {
        message: `Unrecognized part: ${contents.substring(
          this.offset + lenDiff
        )}`
      };
    }

    return {
      meridiem: isAM ? 'AM' : isPM ? 'PM' : null,
      hours: hours.value,
      minutes: minutes.value
    };
  }

  private eof() {
    return this.offset >= this.contents.length;
  }

  public character() {
    return this.contents.charCodeAt(this.offset);
  }

  private read(values: string): boolean;
  private read(...values: string[]): boolean;
  private read(...values: string[]) {
    for (const value of values) {
      if (this.peek(value)) {
        this.offset += value.length;
        return true;
      }
    }
    return false;
  }

  private peek(value: string) {
    return (
      !this.eof() && this.contents.substring(this.offset).startsWith(value)
    );
  }

  private parseInteger() {
    const start = this.offset;
    while (!this.eof() && TimeParser.isInteger(this.character())) {
      this.offset++;
    }
    const n = parseInt(this.contents.substring(start, this.offset), 10);
    if (Number.isNaN(n) || !Number.isFinite(n) || !Number.isInteger(n)) {
      return null;
    }
    return { value: n, digitCount: this.offset - start };
  }
}
